import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Popper, Fade, MenuList, Box } from '@mui/material';
import clsx from 'clsx';
import { omit } from 'lodash-es';
import { IconChevronSmall as IconArrowRight, IconChecked as IconSelected, IconLoading, } from '../../internal/icons';
import MenuItem from './MenuItem';
import { getSubMenuWrapperStyle, getMenuItemStyle, dropdownClasses, } from './style';
import { fadeTimeout, menuItemPositionIdName, menuListPositionIdName, menuPopperPositionIdName, } from './constant';
import { Divider } from '../../Divider';
import { getNextMenuItemElement, getPreviousMenuItemElement, getFirstChildMenuItemElement, getMenuListElement, getSiblingFirstMenuItemElement, getSiblingLastMenuItemElement, } from './dom';
import { ScrollView } from '../../ScrollView';
import { Tooltip } from '../../Tooltip';
function getSuffix(params) {
    const { type, hasChildren, isSelected, loading } = params;
    if (loading) {
        return React.createElement(IconLoading, { style: { fontSize: 16 }, "aria-hidden": "true" });
    }
    if (hasChildren) {
        return React.createElement(IconArrowRight, { style: { fontSize: 16 }, "aria-hidden": "true" });
    }
    if (isSelected && type === 'radio') {
        return (React.createElement(IconSelected, { style: { fontSize: 16 }, className: dropdownClasses.menuItemSuffixActive, "aria-hidden": "true" }));
    }
    return null;
}
function getAccProps(params) {
    const { hasChildren, isSelected, type } = params;
    let accProps = hasChildren
        ? {
            role: 'menuitem',
            'aria-haspopup': 'true',
            // 'aria-expanded': `${isSubMenuOpen}`,
        }
        : {
            role: 'menuitemradio',
            'aria-checked': `${isSelected}`,
        };
    if (type === 'link' || type === 'button') {
        accProps = {
            role: 'menuitem',
        };
    }
    return accProps;
}
export const NestedMenuItem = (props) => {
    const { type, menuId, itemData, parentMenuOpen, children, className, onMenuClick, selectedKeys, keyPath, positionPath, popperProps, menuItemProps, overlayClassName, parentDisabled, disablePortal, closeTopParentMenu, closeParentMenu, autoFocus, onRequestSubItems, maxChildrenWidth, minChildrenWidth, maxChildrenHeight, autoLoading, onCloseSubMenu, onOpenSubMenu, } = props;
    const { label, disabled, key: itemKey, onClick, danger, icon, showTopDivider, showBottomDivider, tooltipProps = {}, } = itemData;
    const menuItemRef = useRef(null);
    const menuContainerRef = useRef(null);
    const popperRef = useRef(null);
    const [internalLoading, setInternalLoading] = useState(false);
    const [isSubMenuOpen, setIsSubMenuOpen] = useState(false);
    const open = isSubMenuOpen && parentMenuOpen;
    const hasChildren = Array.isArray(children) && children.length > 0;
    const subItemsCancellerRef = useRef();
    const handleOpen = useCallback(() => {
        onRequestSubItems?.(() => {
            subItemsCancellerRef.current?.();
            setIsSubMenuOpen(false);
        });
        setIsSubMenuOpen(true);
    }, [onRequestSubItems]);
    useEffect(() => {
        const id = `${[...positionPath].join('-')}`;
        if (open) {
            onOpenSubMenu({ id });
        }
        else {
            onCloseSubMenu({ id });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const handleItemKeyDown = useCallback((e) => {
        const activeElement = menuItemRef.current;
        if (['ArrowDown', 'ArrowUp', 'ArrowRight', 'Enter'].includes(e.key)) {
            e.stopPropagation();
            // If it is an A tag link,
            // pressing the Enter key needs to trigger the original link event
            if (!(e.key === 'Enter' && type === 'link')) {
                e.preventDefault();
            }
            if (e.key === 'ArrowDown') {
                const newActiveDOM = getNextMenuItemElement(activeElement);
                newActiveDOM?.focus();
            }
            else if (e.key === 'ArrowUp') {
                const newActiveDOM = getPreviousMenuItemElement(activeElement);
                newActiveDOM?.focus();
            }
            else {
                // 'ArrowRight', 'Enter'
                handleOpen();
            }
        }
        else if (['ArrowLeft', 'Escape'].includes(e.key)) {
            e.stopPropagation();
            e.preventDefault();
            closeParentMenu?.();
            const listElem = getMenuListElement(activeElement);
            if (menuId === listElem?.getAttribute(menuListPositionIdName)) {
                closeTopParentMenu({ focusTriggerElement: true });
            }
        }
        else if (['Home', 'End', 'PageDown', 'PageUp'].includes(e.key)) {
            e.stopPropagation();
            e.preventDefault();
            let activeDOM = null;
            if (['Home', 'PageUp'].includes(e.key)) {
                activeDOM = getSiblingFirstMenuItemElement(activeElement);
            }
            else {
                activeDOM = getSiblingLastMenuItemElement(activeElement);
            }
            activeDOM?.focus();
        }
        else if (['Space'].includes(e.code) && type === 'link') {
            // If it is an A tag link,
            // pressing the Space key needs to trigger a link click event
            activeElement.click();
        }
    }, [type, handleOpen, closeParentMenu, menuId, closeTopParentMenu]);
    const handleRequestSubItems = useCallback((canceller) => {
        subItemsCancellerRef.current?.();
        subItemsCancellerRef.current = canceller;
    }, []);
    const listUid = `${[...[menuId], ...positionPath].join('-')}`;
    const itemUid = `${menuId}-${[...positionPath].join('-')}`;
    const isSelected = type === 'radio' && !hasChildren && !!selectedKeys?.includes(itemKey);
    // The menu is not scrollable if it has sub menu and the `disablePortal` is `true` because of the overflow: scroll prevents the menu from overflowing.
    const scrollable = !disablePortal;
    const ScrollableAdaptiveContainer = scrollable ? ScrollView : Box;
    const [showTooltip, setShowTooltip] = useState(false);
    return (React.createElement(React.Fragment, null,
        showTopDivider && (React.createElement(Divider, { className: dropdownClasses.menuGroupDivider })),
        React.createElement(Tooltip, { title: showTooltip ? label : undefined, autoFocus: false, ...omit(tooltipProps, 'children') },
            React.createElement(MenuItem, { type: type, itemData: itemData, [menuItemPositionIdName]: `${itemUid}`, ...getAccProps({
                    hasChildren,
                    isSelected,
                    type,
                }), className: clsx(className, menuItemProps?.className), ...menuItemProps, disabled: parentDisabled ?? disabled, selected: isSelected, danger: danger, expend: open, prefixIcon: icon, suffixIcon: getSuffix({
                    type,
                    hasChildren,
                    isSelected,
                    loading: autoLoading && internalLoading,
                }), ...(autoLoading && internalLoading
                    ? {
                        'aria-busy': 'true',
                    }
                    : {}), ref: menuItemRef, onClick: (e) => {
                    e.stopPropagation();
                    if (disabled ?? (autoLoading && internalLoading)) {
                        return;
                    }
                    const event = {
                        key: itemKey,
                        keyPath,
                        data: itemData,
                        domEvent: e,
                    };
                    const clickResult = onClick?.(event);
                    const clickMenuResult = onMenuClick?.(event);
                    const promiseList = [];
                    if (autoLoading) {
                        if (Object.prototype.toString.call(clickResult) ===
                            '[object Promise]') {
                            promiseList.push(clickResult);
                        }
                        if (Object.prototype.toString.call(clickMenuResult) ===
                            '[object Promise]') {
                            promiseList.push(clickMenuResult);
                        }
                    }
                    const afterClick = () => {
                        if (hasChildren) {
                            handleOpen();
                        }
                        else {
                            closeTopParentMenu({ focusTriggerElement: true });
                        }
                    };
                    if (promiseList.length > 0) {
                        setInternalLoading(true);
                        Promise.all(promiseList)
                            .then(() => {
                            setInternalLoading(false);
                            afterClick();
                        })
                            .catch((err) => {
                            setInternalLoading(false);
                            afterClick();
                            // eslint-disable-next-line no-console
                            console.error(err);
                        });
                    }
                    else {
                        afterClick();
                    }
                }, sx: [
                    (theme) => getMenuItemStyle(theme),
                    ...(menuItemProps && Array.isArray(menuItemProps?.sx)
                        ? menuItemProps.sx
                        : [menuItemProps?.sx]),
                ], onKeyDown: handleItemKeyDown, key: itemKey, onMouseEnter: handleOpen, onFocus: () => {
                    menuItemRef.current?.setAttribute('tabindex', '0');
                }, onBlur: () => {
                    menuItemRef.current?.setAttribute('tabindex', '-1');
                }, onMounted: ({ isOverflow }) => {
                    setShowTooltip(isOverflow);
                } },
                label,
                !disabled && hasChildren && (React.createElement(Popper, { ...popperProps, anchorEl: menuItemRef.current, ref: popperRef, open: !!open, sx: [
                        (theme) => getSubMenuWrapperStyle(theme),
                        ...(popperProps && Array.isArray(popperProps?.sx)
                            ? popperProps.sx
                            : [popperProps?.sx]),
                    ], role: "presentation", placement: "right-start", disablePortal: disablePortal, transition: true, className: overlayClassName, [menuPopperPositionIdName]: `${itemUid}`, popperOptions: {
                        onFirstUpdate: () => {
                            if (isSubMenuOpen && parentMenuOpen && autoFocus) {
                                const firstMenuItem = getFirstChildMenuItemElement(popperRef.current);
                                firstMenuItem?.focus();
                            }
                        },
                    } }, ({ TransitionProps }) => (React.createElement(Fade, { ...TransitionProps, timeout: fadeTimeout },
                    React.createElement(ScrollableAdaptiveContainer, { ...(scrollable
                            ? {
                                orientation: 'vertical',
                            }
                            : {}), style: {
                            maxWidth: maxChildrenWidth,
                            minWidth: minChildrenWidth,
                            maxHeight: scrollable ? maxChildrenHeight : 'none',
                        }, className: dropdownClasses.subMenu },
                        React.createElement(MenuList, { ref: menuContainerRef, role: "menu", tabIndex: -1, disablePadding: true, [menuListPositionIdName]: `${listUid}` }, children.map((child) => React.cloneElement(child, {
                            closeParentMenu: () => {
                                setIsSubMenuOpen(false);
                                menuItemRef.current?.focus();
                            },
                            onRequestSubItems: handleRequestSubItems,
                        })))))))))),
        showBottomDivider && (React.createElement(Divider, { className: dropdownClasses.menuGroupDivider }))));
};
